// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  type QueryReturnValue,
  type FetchBaseQueryError,
  type FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';
import {
  type AssetTypeClassifier,
  type AssetsClassifierResp,
  type AssetsComparison,
  type AssetsInfoResp,
  type AssetsSearchResp,
  type FundHoldingsParams,
  type FundHoldingsResp,
} from '@shared/data-access/interfaces/api/ai/assets';
import { assetTypeMap } from '@shared/data-access/transformers/assets/group-positions-by-asset-type';
import {
  type MutualFund,
  type Stock,
} from '@shared/data-access/interfaces/api/ai/screener';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import type { AccountCurrency } from '@shared/data-access/types/brokerage-types';
import {
  assetsRepositorySlice,
  selectAssetsInfoById,
  selectClassificationById,
} from '@shared/data-access/slices/assets-repository';
import { aiApi } from '@shared/data-access/api-base/citec-ai-api';
import { buildAssetsComparison } from '@shared/data-access/transformers/assets/build-assets-comparison';
import { SharedTagTypes } from '@shared/data-access/utils/constants';

export const assetsApi = aiApi.injectEndpoints({
  endpoints: build => ({
    assetsInfo: build.query<
      AssetsInfoResp[],
      {
        params: { detail: boolean; currency?: string };
        payload: string[];
        assetType?: string;
      }
    >({
      queryFn: async (
        { payload: assets, params, assetType = 'stock' },
        { getState, dispatch },
        _extraOptions,
        fetchWithBQ,
      ) => {
        const currency =
          (params.currency as AccountCurrency) ??
          selectAccountCurrency(getState() as any);

        if (!assets.length) return { data: [] };

        const assetsInfo = new Map(
          selectAssetsInfoById(getState() as any, assets, currency).map(item => [
            item.ticker,
            item,
          ]),
        );

        const cachedAssets = Array.from(assetsInfo.values(), item => item.ticker);

        const payload: string[] = assets.filter(
          ticker => !cachedAssets.includes(ticker),
        );

        if (!payload.length)
          return {
            data: Array.from(assetsInfo.values()).map(item => item.details),
          };

        const response = (await fetchWithBQ({
          url: `/assets/${assetType}/info/`,
          method: 'POST',
          body: payload,
          params,
        })) as QueryReturnValue<
          AssetsInfoResp[],
          FetchBaseQueryError,
          FetchBaseQueryMeta
        >;

        if (response.error)
          return {
            error: response.error as FetchBaseQueryError,
          };

        const assetsEntries = response.data.map(item => ({
          ticker: item.ticker,
          details: item,
          currency,
        }));

        const data = Array.from(assetsInfo.values()).concat(assetsEntries);

        dispatch(assetsRepositorySlice.actions.upsertAssetInfo(data));

        return {
          data: data.map(item => item.details),
          meta: response.meta,
        };
      },
      extraOptions: {
        includeCurrency: true,
      },
      providesTags: [
        { type: 'Assets', id: 'Info' },
        SharedTagTypes.CurrencyConversion,
      ],
    }),

    classifier: build.query<AssetsClassifierResp[], { payload: string[] }>({
      queryFn: async (
        { payload: assets },
        { getState, dispatch },
        _extraOptions,
        fetchWithBQ,
      ) => {
        if (!assets.length) return { data: [] };

        const assetsClassifier = new Map(
          selectClassificationById(getState() as any, assets).map(item => [
            item.ticker,
            item,
          ]),
        );

        const cachedAssets = Array.from(
          assetsClassifier.values(),
          item => item.ticker,
        );

        const payload: string[] = assets.filter(
          ticker => !cachedAssets.includes(ticker),
        );

        if (!payload.length)
          return {
            data: Array.from(assetsClassifier.values()).map(
              item => item.classification,
            ),
          };

        const response = (await fetchWithBQ({
          url: 'assets/classifier/',
          method: 'POST',
          body: payload,
        })) as QueryReturnValue<
          AssetsClassifierResp[],
          FetchBaseQueryError,
          FetchBaseQueryMeta
        >;

        if (response.error)
          return {
            error: response.error as FetchBaseQueryError,
          };

        const assetsClassifierEntries = response.data.map(item => ({
          ticker: item.ticker,
          classification: item,
        }));

        const data = Array.from(assetsClassifier.values()).concat(
          assetsClassifierEntries,
        );

        dispatch(assetsRepositorySlice.actions.upsertClassifications(data));

        return {
          data: data.map(item => item.classification),
          meta: response.meta,
        };
      },
      extraOptions: {},
      providesTags: result =>
        result
          ? [
              ...result.map(({ ticker }) => ({
                type: 'Assets' as const,
                id: ticker,
              })),
              { type: 'Assets', id: 'Classifier' },
            ]
          : [{ type: 'Assets', id: 'Classifier' }],
    }),

    search: build.query<
      AssetsSearchResp[],
      {
        ticker: string;
        name: string;
        assetType?: string;
        key_search?: string;
        isin?: string | null;
        details?: boolean;
      }
    >({
      queryFn: async (
        { name, ticker, assetType, key_search, isin, details = true },
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) => {
        const searchResult = (await fetchWithBQ({
          url: 'assets/search/',
          method: 'GET',
          params: {
            ticker,
            name,
            asset_type: assetType,
            key_search,
            isin,
          },
        })) as QueryReturnValue<
          AssetsSearchResp[],
          FetchBaseQueryError,
          FetchBaseQueryMeta
        >;

        if (searchResult.error)
          return {
            error: searchResult.error as FetchBaseQueryError,
          };

        if (details === false)
          return { data: searchResult.data, meta: searchResult.meta };

        const assets = searchResult.data
          .map(asset => ({
            ...asset,
            asset_type: assetTypeMap[asset.asset_type as AssetTypeClassifier],
          }))
          .slice(0, 10);

        const stockTickers = assets
          .filter(({ asset_type }) => asset_type?.includes('stock'))
          .map(({ ticker }) => ticker);

        const mutualFundTickers = assets
          .filter(({ asset_type }) => asset_type?.includes('mutual_fund'))
          .map(({ ticker }) => ticker);

        const getAssetsInfo = (currentAssetType: string, assetTickers: string[]) =>
          _queryApi
            .dispatch(
              assetsApi.endpoints.assetsInfo.initiate({
                params: { detail: true },
                payload: assetTickers,
                assetType: currentAssetType,
              }),
            )
            .unwrap();

        try {
          const [stocksInfo, mutualFundsInfo] = (await Promise.all([
            getAssetsInfo('stock', stockTickers),
            getAssetsInfo('mutual_fund', mutualFundTickers),
          ])) as [Stock[], MutualFund[]];

          const stocksInfoByTicker = new Map(
            stocksInfo.map(assetInfo => [assetInfo.ticker, assetInfo]),
          );

          const mutualFundsInfoByTicker = new Map(
            mutualFundsInfo.map(assetInfo => [assetInfo.ticker, assetInfo]),
          );

          /* istanbul ignore next */
          const data = assets.map(asset => ({
            ...asset,
            url_morningstar: stocksInfoByTicker.get(asset.ticker)?.url_morningstar,
            url_logo:
              asset?.asset_type === 'stock'
                ? stocksInfoByTicker.get(asset.ticker)?.url_logo
                : 'missing',
            dfi_url:
              asset.asset_type === 'mutual_fund'
                ? mutualFundsInfoByTicker.get(asset.ticker)?.dfi_url
                : undefined,
          })) as AssetsSearchResp[];

          return {
            data,
          };
        } catch (error) {
          return {
            error: error as FetchBaseQueryError,
          };
        }
      },
      extraOptions: {},
      providesTags: result =>
        result
          ? [
              ...result.map(({ ticker }) => ({
                type: 'Assets' as const,
                id: ticker,
              })),
              { type: 'Assets' as const, id: 'Search' },
            ]
          : [{ type: 'Assets', id: 'Search' }],
    }),

    fundHoldings: build.query<
      FundHoldingsResp,
      {
        params: FundHoldingsParams;
        assetType: string;
      }
    >({
      query: ({ params, assetType }) => ({
        url: `assets/${assetType}/holdings/`,
        method: 'GET',
        params,
      }),
      extraOptions: {},
      providesTags: [{ type: 'Assets', id: 'FundHoldings' }],
    }),

    comparison: build.query<
      [AssetsComparison, AssetsInfoResp[]],
      { tickers: string[]; hist_w: number; benchmark: string }
    >({
      queryFn: async (
        { tickers, hist_w, benchmark },
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) => {
        const payload = Array.from(
          new Set([benchmark, ...tickers].sort((x, y) => x.localeCompare(y))),
        );

        const response = (await fetchWithBQ({
          url: 'assets/comparison/',
          method: 'POST',
          body: payload,
          params: { hist_w },
        })) as QueryReturnValue<
          AssetsComparison,
          FetchBaseQueryError,
          FetchBaseQueryMeta
        >;

        if (response.error) return { error: response.error as FetchBaseQueryError };

        const classifierData = await _queryApi
          .dispatch(assetsApi.endpoints.classifier.initiate({ payload }))
          .unwrap();

        const getAssetsInfo = (assetType: string, assetTickers: string[]) =>
          _queryApi
            .dispatch(
              assetsApi.endpoints.assetsInfo.initiate({
                params: { detail: true },
                payload: assetTickers,
                assetType,
              }),
            )
            .unwrap();

        const result = await buildAssetsComparison(
          classifierData,
          getAssetsInfo,
          benchmark,
        );

        return {
          data: [response.data, result],
        };
      },
      extraOptions: {
        includeCurrency: true,
        useTrackPromise: true,
      },
      providesTags: [
        { type: 'Assets', id: 'Comparison' },
        SharedTagTypes.CurrencyConversion,
      ],
    }),
  }),
});
